// Sending a control's action and following it (device-controls WEB-CMD-01 to -04, WEB-SET-03). The live socket has
// no command messages yet (live.yaml 0.1.0), so an open command is read every few seconds until it ends.
import { queryOptions, useQueries, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, orgHeader, unwrap } from "../../../api/client";
import type { components } from "../../../api/generated/vision-api";
import { ApiError } from "../../../api/problem";
import { useScenes } from "../../scenes/useScenes";
import type { Widget, WidgetAction, WidgetInput } from "../../../widgets/types";
import type { PageDocument } from "../page/profile";
import { settingsQuery } from "./queries";
import {
  OPEN,
  pollDelay,
  reportedAsAsked,
  resolveAction,
  widgetState,
  type CommandState,
  type Resolved,
} from "./resolve";

export type Command = components["schemas"]["Command"];

export const commandQuery = (orgId: string, device: string, id: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/devices/{device_id}/commands/{command_id}", device, id],
    queryFn: () =>
      unwrap(
        apiClient("customers").GET("/v1/devices/{device_id}/commands/{command_id}", {
          params: { path: { device_id: device, command_id: id }, header: orgHeader(orgId) },
        }),
      ),
    // Read again from 2 s, backing off to 10 s, until the command ends.
    refetchInterval: (q) =>
      q.state.data && !OPEN.has(q.state.data.state) ? false : pollDelay(q.state.dataUpdateCount),
  });

type Sent = { device: string; key: string; value: unknown; command: Command };
export type Asking = { action: Extract<WidgetAction, { kind: "command" | "setting" }>; resolved: Resolved };

/** A refused send in words: rate limit, commissioning, permission, or the API's message. */
export function sendError(e: unknown, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (!(e instanceof ApiError)) return t("controls.error.unknown");
  if (e.status === 429) return t("controls.error.rateLimited", { count: e.retryAfterMinutes ?? 1 });
  if (e.code === "commissioning_required") return t("controls.error.commissioning");
  if (e.code === "confirm_required") return t("controls.error.confirm");
  if (e.status === 403) return t("controls.error.forbidden");
  return e.message;
}

type ActionInfo = components["schemas"]["ActionInfo"];

/**
 * `docFor` gives a device's page document (what a control sends); `infoFor` its page's ActionInfo for a key (the
 * confirm text and name of a guarded command, vision-api 0.19.0). `withScenes` reads the manual automations for the
 * page's scene buttons (alarms-and-scenes WEB-SCN-01).
 */
export function useDeviceActions(
  orgId: string,
  docFor: (device: string) => PageDocument | undefined,
  infoFor: (device: string, key: string) => ActionInfo | undefined = () => undefined,
  withScenes = false,
) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const scenes = useScenes(orgId, withScenes);
  const [sent, setSent] = useState<Record<string, Sent>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [asking, setAsking] = useState<Asking>();
  // Sends not answered yet, with their asked value: the control waits from the click, so a second click cannot send
  // the same command twice (the API has no idempotency key).
  const [sending, setSending] = useState<Record<string, unknown>>({});
  const inFlight = useRef(new Set<string>());
  const entries = Object.entries(sent);
  const polls = useQueries({
    queries: entries.map(([, s]) => commandQuery(orgId, s.device, s.command.id)),
  });
  const now: Record<string, Command> = Object.fromEntries(entries.map(([k, s], i) => [k, polls[i]?.data ?? s.command]));

  // A command that ended well: read the values again so the control shows what the device reports now.
  const done = Object.entries(now)
    .filter(([, c]) => c.state === "executed")
    .map(([k]) => k)
    .join(",");
  useEffect(() => {
    if (!done) return;
    void qc.invalidateQueries({ queryKey: ["org", orgId, "/v1/latest"] });
    void qc.invalidateQueries({ queryKey: ["org", orgId, "/v1/devices/{device_id}/settings"] });
    void qc.invalidateQueries({ queryKey: ["org", orgId, "/v1/devices/{device_id}/commands"] });
  }, [done, orgId, qc]);

  async function send(action: Asking["action"], resolved: Resolved, params: Record<string, unknown> = {}) {
    const k = `${action.device}/${action.key}`;
    if (inFlight.current.has(k)) return;
    inFlight.current.add(k);
    setSending((x) => ({ ...x, [k]: action.value }));
    setErrors((x) => Object.fromEntries(Object.entries(x).filter(([key]) => key !== k)));
    try {
      let command: Command;
      if (resolved.kind === "command") {
        const all = { ...resolved.params, ...params };
        command = await unwrap(
          apiClient("customers").POST("/v1/devices/{device_id}/commands", {
            params: { path: { device_id: action.device }, header: orgHeader(orgId) },
            body: {
              command: resolved.command,
              ...(Object.keys(all).length ? { params: all } : {}),
              ...(resolved.guarded ? { confirm: true } : {}),
            },
          }),
        );
      } else {
        const change = await unwrap(
          apiClient("customers").PUT("/v1/devices/{device_id}/settings", {
            params: { path: { device_id: action.device }, header: orgHeader(orgId) },
            body: { values: resolved.values },
          }),
        );
        // The Settings section shows the change as pending at once, and so polls it.
        qc.setQueryData(settingsQuery(orgId, action.device).queryKey, (old) =>
          old ? { items: old.items.map((x) => change.settings.find((c) => c.key === x.key) ?? x) } : old,
        );
        command = change.command;
      }
      setSent((s) => ({ ...s, [k]: { device: action.device, key: action.key, value: action.value, command } }));
    } catch (e) {
      setErrors((x) => ({ ...x, [k]: sendError(e, t) }));
    } finally {
      inFlight.current.delete(k);
      setSending((x) => Object.fromEntries(Object.entries(x).filter(([key]) => key !== k)));
    }
  }

  /**
   * A control asked for something: guarded commands and missing params go through the dialog first. Without the
   * device's page document nothing is sent, since start cannot be told from stop nor the params known.
   */
  function onAction(action: WidgetAction) {
    if (action.kind === "automation") {
      scenes.ask(action.id);
      return;
    }
    const doc = docFor(action.device);
    if (!doc) {
      setErrors((x) => ({ ...x, [`${action.device}/${action.key}`]: t("controls.error.notReady") }));
      return;
    }
    let resolved = resolveAction(doc, action);
    if (resolved.kind === "command") {
      const info = infoFor(action.device, resolved.command);
      resolved = {
        ...resolved,
        ...(info?.confirm ? { confirmText: info.confirm } : {}),
        ...(info?.name ? { label: info.name } : {}),
      };
    }
    if (resolved.kind === "command" && (resolved.guarded || resolved.ask.length)) setAsking({ action, resolved });
    else void send(action, resolved);
  }

  async function cancel(k: string) {
    const s = sent[k];
    if (!s) return;
    try {
      await unwrap(
        apiClient("customers").DELETE("/v1/devices/{device_id}/commands/{command_id}", {
          params: { path: { device_id: s.device, command_id: s.command.id }, header: orgHeader(orgId) },
        }),
      );
      void qc.invalidateQueries({ queryKey: commandQuery(orgId, s.device, s.command.id).queryKey });
    } catch (e) {
      setErrors((x) => ({
        ...x,
        [k]: e instanceof ApiError && e.status === 409 ? t("controls.error.alreadyDelivered") : sendError(e, t),
      }));
    }
  }

  /**
   * The widget input with each sent action's state and asked value (widget-fixture ActionState). Controls of a device
   * whose page document is not read (yet) stay read-only. With `widget`, its reported value ends the wait once it
   * matches the asked one.
   */
  function withActions(input: WidgetInput, widget?: Widget): WidgetInput {
    const actions = { ...input.actions };
    for (const [k, a] of Object.entries(actions)) {
      if (a.allowed && !docFor(k.slice(0, k.indexOf("/")))) actions[k] = { ...a, allowed: false };
    }
    for (const [k, s] of entries) {
      const base = actions[k];
      const c = now[k];
      if (!base || !c) continue;
      const open = OPEN.has(c.state) && !(widget && reportedAsAsked(widget, input, s.value, c.requested_at));
      actions[k] = {
        ...base,
        state: OPEN.has(c.state) && !open ? "executed" : widgetState(c.state as CommandState),
        ...(open && (typeof s.value === "boolean" || typeof s.value === "number" || typeof s.value === "string")
          ? { value: s.value }
          : {}),
      };
    }
    for (const [k, value] of Object.entries(sending)) {
      const base = actions[k];
      if (!base) continue;
      actions[k] = {
        ...base,
        state: "queued",
        ...(typeof value === "boolean" || typeof value === "number" || typeof value === "string" ? { value } : {}),
      };
    }
    return { ...input, actions, ...(scenes.list ? { automations: scenes.list } : {}) };
  }

  return {
    onAction,
    send,
    cancel,
    withActions,
    asking,
    closeAsking: () => setAsking(undefined),
    /** By "<device>/<key>": the latest command of each control, and a refused send's message. */
    commands: now,
    /** By "<device>/<key>": sends not answered yet. */
    sending,
    docFor,
    errors,
    /** The page's scene buttons: their automations, the confirmation and each run's state. */
    scenes,
    /** A scene button whose run is in flight cannot be pressed again (WEB-SCN-04). */
    busy: (w: Widget) => w.type === "scene_button" && !!w.automation && scenes.status[w.automation] === "starting",
  };
}

export type DeviceActions = ReturnType<typeof useDeviceActions>;
